// MIT License - Copyright (c) 2026 Doug McNall
import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { ThemeProvider } from './context/ThemeContext';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Header';
import Footer from './components/Footer';
import CartSidebar from './components/CartSidebar';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';
import './styles/light-theme.css';
import './styles/login.css';
import './styles/offers.css';
import './styles/help.css';
import './styles/account.css';
import './styles/products.css';
import './styles/product-detail.css';
import './styles/legal.css';
import './styles/categories.css';
import './styles/recipes.css';
import './styles/recipe-detail.css';
import './components/ErrorBoundary.css';

// Lazy load page components for code splitting
const Home = lazy(() => import('./pages/Home'));
const Product = lazy(() => import('./pages/Product'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Categories = lazy(() => import('./pages/Categories'));
const Recipes = lazy(() => import('./pages/Recipes'));
const RecipeDetail = lazy(() => import('./pages/RecipeDetail'));
const Offers = lazy(() => import('./pages/Offers'));
const Help = lazy(() => import('./pages/Help'));
const Account = lazy(() => import('./pages/Account'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const Support = lazy(() => import('./pages/Support'));
const MyTickets = lazy(() => import('./pages/MyTickets'));
// Admin app is separate - no longer imported here

// Shop component that wraps all shop routes
const ShopApp = () => (
  <div className="super_container">
    <CartSidebar />
    <Header />
    <div id="divMainBody">
      <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/product" element={<Product />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/recipes" element={<Recipes />} />
          <Route path="/recipes/:id" element={<RecipeDetail />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/support" element={<Support />} />
          <Route path="/support/tickets" element={<MyTickets />} />
          <Route path="/support/tickets/:id" element={<MyTickets />} />
          <Route path="/account" element={<Account />} />
          <Route path="/account/orders/:id" element={<OrderDetail />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/help" element={<Help />} />
          <Route path="*" element={
            <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-primary)' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>🌶️</div>
              <h1>Page Not Found</h1>
              <p style={{ color: 'var(--text-secondary)' }}>The page you're looking for doesn't exist.</p>
              <a href="/shop" style={{ color: 'var(--button-primary)' }}>Back to Home</a>
            </div>
          } />
        </Routes>
      </Suspense>
    </div>
    <Footer />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
            <Router>
              <Suspense fallback={<LoadingSpinner message="Loading..." />}>
                <Routes>
                  {/* Shop routes only */}
                  <Route path="/shop/*" element={<ShopApp />} />
                  
                  {/* Admin tool is a separate app - redirect to it */}
                  <Route path="/admin-tool/*" element={
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                      <h2>Admin Tool</h2>
                      <p>The admin tool is a separate application.</p>
                      <p>Please run the admin app separately or access it at its dedicated URL.</p>
                    </div>
                  } />
                  
                  {/* Redirect root to shop */}
                  <Route path="/" element={<Navigate to="/shop" replace />} />
                </Routes>
              </Suspense>
            </Router>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
